import "./style.scss";
import { memo, useEffect, useState } from "react";
import CustomSingleSelect from "@/src/components/CustomUtils/CustomSingleSelect/CustomSingleSelect";
import CustomDate from "../../CustomUtils/CustomDate/CustomDate";
import DefaultSelector from "@/src/components/CustomUtils/DefaultSelector/DefaultSelector";
import CustomIncomeManagement from "@/src/components/CustomUtils/CustomIncomeManagement/CustomIncomeManagement";
import CustomComments from "@/src/components/CustomUtils/CustomComments/CustomComments";
import CustomCalculatePopUp, {
  formatTuitionFeeDisplay,
} from "@/src/components/CustomUtils/CustomCalculatePopUp/CustomCalculatePopUp";
import { IApplyInfoComment } from "@/src/types/applicationOnline";
import {
  ApplicantAccountingFormulaType,
  IAccountFormula,
  IOfficeAccountingIncomeRecord,
} from "@/src/types/officeAccounting";
import { getLastOfficeAccountingIncomeRecordDate } from "@/src/utils/officeAccountingMappers";
import {
  getOfficeAccountingFormulaColumnConfig,
  isOfficeAccountingFormulaColumn,
} from "@/src/constants/officeAccountingFormulaConfig";
import {
  OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED,
  OFFICE_ACCOUNTING_PAYMENT_FEE_STATUS_COLUMNS,
  officeAccountingAdmissionPaymentStatusConst,
  officeAccountingGeneralStatusConst,
  officeAccountingPaymentFeeStatusConst,
  resolveOfficeAccountingAdmissionPaymentStatusId,
  resolveOfficeAccountingAdmissionPaymentStatusLabel,
  resolveOfficeAccountingCampusLabel,
  resolveOfficeAccountingGeneralStatusId,
  resolveOfficeAccountingGeneralStatusLabel,
  resolveOfficeAccountingPaymentFeeStatusId,
  resolveOfficeAccountingPaymentFeeStatusLabel,
} from "@/src/constants/officeAccountingTableConfig";
import {
  resolveStudentStatusId,
  resolveStudentStatusLabel,
  studentStatusConst,
} from "@/src/constants/studentStatusConsts";
import { OpenOfficeAccountingFullNameParams } from "../openOfficeAccountingFullNamePopUp";
import DefaultInputChange from "@/src/components/CustomUtils/DefaultInputChange/DefaultInputChange";
import { admissionSemesterOptions } from "@/src/constants/documentPreparationPopUpConsts";
import { buildIntakeOptionsWithStored } from "@/src/utils/intakeCalc";
import { Countries } from "@/src/constants/globalConsts";
import { statusConsts } from "@/src/constants/statusConsts";
import ArrowSvg from "@/src/svg/applicationsPage/APPopUp/Documents/ArrowSvg";
import { OfficeAccountingSvgExport } from "@/src/svg/officeAccountingPage/OfficeAccountingSvgExport";
import { OFFICE_ACCOUNTING_CURRENT_SEMESTER_OPTIONS } from "@/src/utils/officeAccountingTotalAmountUsd";

type BodyColumn = {
  id: string;
  value: string;
};

type CellContentProps = {
  column: BodyColumn;
  onChange: (value: string) => void;
  rowId?: string;
  studentInfoId?: number;
  applicantId?: number;
  commentsList?: IApplyInfoComment[];
  accountFormulas?: Partial<Record<ApplicantAccountingFormulaType, IAccountFormula | null>>;
  invitationFee?: string;
  totalAmountUsd?: string;
  totalMissedClassesEur?: string;
  totalNostrificationsEur?: string;
  invitationFeePaidRecords?: IOfficeAccountingIncomeRecord[];
  paymentFactUsdRecords?: IOfficeAccountingIncomeRecord[];
  paymentFactEurRecords?: IOfficeAccountingIncomeRecord[];
  generatedAdmissionNos?: string[];
  generatedInvoiceNos?: string[];
  onRefreshComments?: () => void | Promise<void>;
  onOpenFullNamePopUp?: (params: OpenOfficeAccountingFullNameParams) => void;
};

const {
  ArrowSvg: OaArrowSvg,
  CalculateSvg,
  DateSvg,
  EyeSvg,
  PencilSvg,
  PlusSvg,
} = OfficeAccountingSvgExport;

/** Edit icon inside `cell-content-edit` for Office Accounting tbody cells. */
const getOfficeAccountingEditIcon = (columnId: string) => {
  if (isOfficeAccountingFormulaColumn(columnId)) return <CalculateSvg />;

  switch (columnId) {
    case "comments":
    case "country":
    case "fieldOfStudy":
    case "admissionSemester":
    case "intake":
    case "currentSemester":
    case "admissionNo":
    case "invoiceNumber":
    case "invitationFee":
      return <OaArrowSvg />;
    case "dateOfBirth":
    case "passportIssued":
    case "validPassport":
    case "ifDate":
      return <DateSvg />;
    case "passportNo":
      return <EyeSvg />;
    case "phone":
    case "email":
    case "managersEmail":
    case "agentsEmail":
      return <PencilSvg />;
    case "invitationFeePaid":
    case "paymentFactUsd":
    case "paymentFactEur":
      return <PlusSvg />;
    default:
      return <PencilSvg />;
  }
};

const MANAGERS_EMAIL_MOCK = ["ieu@gmail.com"];

const AGENTS_EMAIL_MOCK = ["fcdhfj@gmail.com"];

const FIELD_OF_STUDY_OPTIONS = [
  "Medicine on campus",
  "Pediatrics on campus",
  "Dentistry on campus",
  "Pharmacy on campus",
  "Med Lab Science on campus",
  "Nursing on campus",
  "Public Health on campus",
  "Physiotherapy on campus",
  "Bsc non med on campus",
  "Msc non med on campus",
  "PG Medical",
  "Preparatory Department on campus",
  "Medicine online",
  "Dentistry online",
  "Nursing online",
  "Pharmacy online",
  "Med Lab science online",
  "Public Health online",
  "Physiotherapy online",
  "Bsc non med online",
  "Msc non med online",
  "Preparatory Department online",
];

const ADMISSION_SEMESTER_OPTIONS = admissionSemesterOptions.map((option) => option.label);

const getIntakeOptions = (storedValue?: string) =>
  buildIntakeOptionsWithStored(storedValue).map((option) => option.label);

const STATUS_COLUMNS = new Set([
  "statusAl",
  "statusEmail",
  "statusEdbo",
]);

const CellContent = ({
  column,
  onChange,
  rowId,
  studentInfoId,
  applicantId,
  commentsList = [],
  accountFormulas = {},
  invitationFee = "",
  totalAmountUsd = "",
  totalMissedClassesEur = "",
  totalNostrificationsEur = "",
  invitationFeePaidRecords = [],
  paymentFactUsdRecords = [],
  paymentFactEurRecords = [],
  generatedAdmissionNos = [],
  generatedInvoiceNos = [],
  onRefreshComments,
  onOpenFullNamePopUp,
}: CellContentProps) => {
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isIfDateOpen, setIsIfDateOpen] = useState(false);
  const [isValidPassportOpen, setIsValidPassportOpen] = useState(false);
  const [isPassportIssuedOpen, setIsPassportIssuedOpen] = useState(false);
  const [isAdmissionSemesterOpen, setIsAdmissionSemesterOpen] = useState(false);
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [isCurrentSemesterOpen, setIsCurrentSemesterOpen] = useState(false);
  const [isAdmissionNoOpen, setIsAdmissionNoOpen] = useState(false);
  const [isInvoiceNumberOpen, setIsInvoiceNumberOpen] = useState(false);
  const [isManagersEmailOpen, setIsManagersEmailOpen] = useState(false);
  const [isAgentsEmailOpen, setIsAgentsEmailOpen] = useState(false);
  const [isFieldOfStudyOpen, setIsFieldOfStudyOpen] = useState(false);
  const [isInvitationFeePaidOpen, setIsInvitationFeePaidOpen] = useState(false);
  const [isPaymentFactUsdOpen, setIsPaymentFactUsdOpen] = useState(false);
  const [isPaymentFactEurOpen, setIsPaymentFactEurOpen] = useState(false);
  const [isInvitationFeeOpen, setIsInvitationFeeOpen] = useState(false);
  const [isPhoneOpen, setIsPhoneOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isStatusAdmissionPaymentOpen, setIsStatusAdmissionPaymentOpen] = useState(false);
  const [isPaymentFeeStatusOpen, setIsPaymentFeeStatusOpen] = useState(false);
  const [isGeneralStatusOpen, setIsGeneralStatusOpen] = useState(false);
  const [isStudentStatusOpen, setIsStudentStatusOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [countryList, setCountryList] = useState<string[]>(Countries);
  const [managersEmail, setManagersEmail] = useState<string[]>(MANAGERS_EMAIL_MOCK);
  const [agentsEmail, setAgentsEmail] = useState<string[]>(AGENTS_EMAIL_MOCK);
  const [fieldOfStudy, setFieldOfStudy] = useState<string[]>(FIELD_OF_STUDY_OPTIONS);
  const [openFormulaColumnId, setOpenFormulaColumnId] = useState<string | null>(null);

  const handleOpenFullNamePopUp = () => {
    if (!applicantId || !studentInfoId) return;
    onOpenFullNamePopUp?.({ applicantId, studentInfoId });
  };

  const handleOpenDocumentsPopUp = () => {
    if (!applicantId || !studentInfoId) return;
    onOpenFullNamePopUp?.({ applicantId, studentInfoId, initialTabId: "documents" });
  };

  useEffect(() => {
    if (column.id !== "studentStatus" || !isStudentStatusOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".student-status")) {
        setIsStudentStatusOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [column.id, isStudentStatusOpen]);

  useEffect(() => {
    if (column.id !== "generalStatus" || !isGeneralStatusOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".general-status")) {
        setIsGeneralStatusOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [column.id, isGeneralStatusOpen]);

  useEffect(() => {
    if (column.id !== "statusAdmissionPayment" || !isStatusAdmissionPaymentOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".status-admission-payment")) {
        setIsStatusAdmissionPaymentOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [column.id, isStatusAdmissionPaymentOpen]);

  useEffect(() => {
    if (!OFFICE_ACCOUNTING_PAYMENT_FEE_STATUS_COLUMNS.has(column.id) || !isPaymentFeeStatusOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".payment-fee-status")) {
        setIsPaymentFeeStatusOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [column.id, isPaymentFeeStatusOpen]);

  const handleGeneralStatusSelect = (label: string) => {
    onChange(label === OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED ? "" : label);
    setIsGeneralStatusOpen(false);
  };

  // FULL NAME
  if (column.id === "fullName") {
    return (
      <div className="cell-content full-name clicable" onClick={handleOpenFullNamePopUp}>
        <div className="full-name-cell">
          <p>{column.value}</p>
          <ArrowSvg />
        </div>
      </div>
    );
  }

  // CAMPUS — display only; filter is in column header (`office` query)
  if (column.id === "campus") {
    return (
      <div className="cell-content campus-select">
        <span className="status-label">{resolveOfficeAccountingCampusLabel(column.value)}</span>
      </div>
    );
  }

  // COUNTRY
  if (column.id === "country") {
    return (
      <div className="cell-content country">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsCountryOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isCountryOpen && (
          <CustomSingleSelect
            title={"Choose country"}
            data={countryList}
            isSearch={true}
            allowAdd={false}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsCountryOpen(false)}
            onAddItem={(value) => setCountryList((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // COMMENTS
  if (column.id === "comments") {
    return (
      <div className="cell-content cell-content-comments">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsCommentsOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isCommentsOpen && studentInfoId != null && studentInfoId > 0 && (
          <CustomComments
            title="Comments"
            comments={commentsList}
            accountingId={studentInfoId}
            onRefresh={onRefreshComments}
            onClose={() => setIsCommentsOpen(false)}
          />
        )}
      </div>
    );
  }
  // DATE OF BIRTH
  if (column.id === "dateOfBirth") {
    return (
      <div className="cell-content date">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsDateOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isDateOpen && <CustomDate value={column.value} onChange={onChange} onClose={() => setIsDateOpen(false)} />}
      </div>
    );
  }
  // STUDENT STATUS — same badge + menu pattern as Status / general_status
  if (column.id === "studentStatus") {
    const statusId = resolveStudentStatusId(column.value);
    const statusLabel = resolveStudentStatusLabel(column.value);

    return (
      <div className="cell-content student-status">
        <div
          className={`status-option ${statusId}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsStudentStatusOpen((prev) => !prev);
          }}
        >
          <span className="status-label">{statusLabel}</span>
        </div>
        {isStudentStatusOpen && (
          <div className="status-menu">
            {studentStatusConst.map(({ id, label }) => (
              <div
                key={id}
                className={`status-option ${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(id);
                  setIsStudentStatusOpen(false);
                }}
              >
                <span className="status-label">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  // PASSPORT NO — opens userInfo popup on Documents; value is read-only in the table
  if (column.id === "passportNo") {
    return (
      <div className="cell-content passport-no clicable" onClick={handleOpenDocumentsPopUp}>
        <div className="full-name-cell">
          <p>{column.value}</p>
          <EyeSvg />
        </div>
      </div>
    );
  }
  // PASSPORT ISSUED DATE
  if (column.id === "passportIssued") {
    return (
      <div className="cell-content passport-issued">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPassportIssuedOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isPassportIssuedOpen && <CustomDate value={column.value} onChange={onChange} onClose={() => setIsPassportIssuedOpen(false)} />}
      </div>
    );
  }
  // VALID PASSPORT DATE
  if (column.id === "validPassport") {
    return (
      <div className="cell-content valid-passport">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsValidPassportOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isValidPassportOpen && <CustomDate value={column.value} onChange={onChange} onClose={() => setIsValidPassportOpen(false)} />}
      </div>
    );
  }
  // PHONE
  if (column.id === "phone") {
    return (
      <div className="cell-content phone">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPhoneOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isPhoneOpen && (
          <DefaultInputChange title={"Enter phone number"} value={column.value} onChange={onChange} onClose={() => setIsPhoneOpen(false)} />
        )}
      </div>
    );
  }
  // COST OF THE 1–12 SEMESTER / PG/PhD / expenses — handled by formula columns below
  // EMAIL
  if (column.id === "email") {
    return (
      <div className="cell-content email">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsEmailOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isEmailOpen && (
          <DefaultInputChange title={"Enter email"} value={column.value} onChange={onChange} onClose={() => setIsEmailOpen(false)} />
        )}
      </div>
    );
  }
  // MANAGERS EMAIL
  if (column.id === "managersEmail") {
    return (
      <div className="cell-content managers-email">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsManagersEmailOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isManagersEmailOpen && (
          <CustomSingleSelect
            title={"Choose manager email"}
            data={managersEmail}
            isSearch={true}
            allowAdd={true}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsManagersEmailOpen(false)}
            onAddItem={(value) => setManagersEmail((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // AGENTS EMAIL
  if (column.id === "agentsEmail") {
    return (
      <div className="cell-content agents-email">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsAgentsEmailOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isAgentsEmailOpen && (
          <CustomSingleSelect
            title={"Choose agent email"}
            data={agentsEmail}
            isSearch={true}
            allowAdd={true}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsAgentsEmailOpen(false)}
            onAddItem={(value) => setAgentsEmail((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // FIELD OF STUDY
  if (column.id === "fieldOfStudy") {
    return (
      <div className="cell-content field-of-study">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsFieldOfStudyOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isFieldOfStudyOpen && (
          <CustomSingleSelect
            title={"Choose field of study"}
            data={fieldOfStudy}
            isSearch={true}
            allowAdd={true}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsFieldOfStudyOpen(false)}
            onAddItem={(value) => setFieldOfStudy((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // ADMISSION SEMESTER
  if (column.id === "admissionSemester") {
    return (
      <div className="cell-content admission-semester">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsAdmissionSemesterOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isAdmissionSemesterOpen && (
          <CustomSingleSelect
            title={"Choose admission semester"}
            data={ADMISSION_SEMESTER_OPTIONS}
            isSearch={true}
            allowAdd={false}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsAdmissionSemesterOpen(false)}
          />
        )}
      </div>
    );
  }
  // INTAKE
  if (column.id === "intake") {
    return (
      <div className="cell-content intake">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsIntakeOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isIntakeOpen && (
          <CustomSingleSelect
            title={"Choose intake"}
            data={getIntakeOptions(column.value)}
            isSearch={true}
            allowAdd={false}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsIntakeOpen(false)}
          />
        )}
      </div>
    );
  }
  // CURRENT SEMESTER
  if (column.id === "currentSemester") {
    return (
      <div className="cell-content current-semester">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsCurrentSemesterOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isCurrentSemesterOpen && (
          <CustomSingleSelect
            title={"Choose current semester"}
            data={[...OFFICE_ACCOUNTING_CURRENT_SEMESTER_OPTIONS]}
            isSearch={false}
            allowAdd={false}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsCurrentSemesterOpen(false)}
          />
        )}
      </div>
    );
  }
  // ADMISSION NO — informational list from generated_docs (document_type=admission)
  if (column.id === "admissionNo") {
    return (
      <div className="cell-content admission-no">
        <p>{column.value}</p>
        {generatedAdmissionNos.length > 0 && (
          <>
            <div className="cell-content-edit" onClick={() => setIsAdmissionNoOpen(true)}>
              {getOfficeAccountingEditIcon(column.id)}
            </div>
            {isAdmissionNoOpen && (
              <CustomSingleSelect
                title="Admission No"
                data={generatedAdmissionNos}
                isSearch={false}
                allowAdd={false}
                readOnly
                columnValue={column.value}
                onClose={() => setIsAdmissionNoOpen(false)}
              />
            )}
          </>
        )}
      </div>
    );
  }
  // STATUS (general_status) — same badge + menu pattern as Online applications
  if (column.id === "generalStatus") {
    const statusId = resolveOfficeAccountingGeneralStatusId(column.value);
    const statusLabel = resolveOfficeAccountingGeneralStatusLabel(column.value);

    return (
      <div className="cell-content general-status">
        <div
          className={`status-option ${statusId}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsGeneralStatusOpen((prev) => !prev);
          }}
        >
          <span className="status-label">{statusLabel}</span>
        </div>
        {isGeneralStatusOpen && (
          <div className="status-menu">
            {officeAccountingGeneralStatusConst.map(({ id, label }) => (
              <div
                key={id}
                className={`status-option ${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGeneralStatusSelect(label);
                }}
              >
                <span className="status-label">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  // STATUS ADMISSION PAYMENT
  if (column.id === "statusAdmissionPayment") {
    const statusId = resolveOfficeAccountingAdmissionPaymentStatusId(column.value);
    const statusLabel = resolveOfficeAccountingAdmissionPaymentStatusLabel(column.value);

    return (
      <div className="cell-content status-admission-payment">
        <div
          className={`status-option ${statusId}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsStatusAdmissionPaymentOpen((prev) => !prev);
          }}
        >
          <span className="status-label">{statusLabel}</span>
        </div>
        {isStatusAdmissionPaymentOpen && (
          <div className="status-menu">
            {officeAccountingAdmissionPaymentStatusConst.map(({ id, label }) => (
              <div
                key={id}
                className={`status-option ${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(label);
                  setIsStatusAdmissionPaymentOpen(false);
                }}
              >
                <span className="status-label">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  // STATUS INVITATION FEE / BALANCE STATUS USD / STATUS NOSTRIFICATION
  if (OFFICE_ACCOUNTING_PAYMENT_FEE_STATUS_COLUMNS.has(column.id)) {
    const statusId = resolveOfficeAccountingPaymentFeeStatusId(column.value);
    const statusLabel = resolveOfficeAccountingPaymentFeeStatusLabel(column.value);

    return (
      <div className="cell-content payment-fee-status">
        <div
          className={`status-option ${statusId}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsPaymentFeeStatusOpen((prev) => !prev);
          }}
        >
          <span className="status-label">{statusLabel}</span>
        </div>
        {isPaymentFeeStatusOpen && (
          <div className="status-menu">
            {officeAccountingPaymentFeeStatusConst.map(({ id, label }) => (
              <div
                key={id}
                className={`status-option ${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(label);
                  setIsPaymentFeeStatusOpen(false);
                }}
              >
                <span className="status-label">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  // INVOICE № — informational list from generated_docs (document_type=invoice)
  if (column.id === "invoiceNumber") {
    return (
      <div className="cell-content invoice-number">
        <p>{column.value}</p>
        {generatedInvoiceNos.length > 0 && (
          <>
            <div className="cell-content-edit" onClick={() => setIsInvoiceNumberOpen(true)}>
              {getOfficeAccountingEditIcon(column.id)}
            </div>
            {isInvoiceNumberOpen && (
              <CustomSingleSelect
                title="Invoice №"
                data={generatedInvoiceNos}
                isSearch={false}
                allowAdd={false}
                readOnly
                columnValue={column.value}
                onClose={() => setIsInvoiceNumberOpen(false)}
              />
            )}
          </>
        )}
      </div>
    );
  }
  // TUITION FEE
  if (column.id === "tuitionFee") {
    return (
      <div className="cell-content tuition-fee">
        <p>{column.value}</p>
      </div>
    );
  }
  // FORMULA COLUMNS — Preparatory Department → Nostrification (calculate popup)
  if (isOfficeAccountingFormulaColumn(column.id)) {
    const formulaConfig = getOfficeAccountingFormulaColumnConfig(column.id)!;
    const formulaBlock = accountFormulas[formulaConfig.type] ?? null;
    const formulaValue = formulaBlock?.formula ?? "";
    const displayValue =
      formatTuitionFeeDisplay(formulaValue, formulaConfig.currency) ||
      formatTuitionFeeDisplay(column.value, formulaConfig.currency) ||
      column.value;

    return (
      <div className={`cell-content formula-column formula-${column.id}`}>
        <p>{displayValue}</p>
        <div className="cell-content-edit" onClick={() => setOpenFormulaColumnId(column.id)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {openFormulaColumnId === column.id && (
          <CustomCalculatePopUp
            title="Select an option"
            value={formulaValue || column.value}
            currency={formulaConfig.currency}
            formulaComments={formulaBlock?.comments ?? []}
            applicantAccountingId={studentInfoId}
            applicantId={applicantId}
            formulaType={formulaConfig.type}
            onChange={onChange}
            onRefresh={onRefreshComments}
            onClose={() => setOpenFormulaColumnId(null)}
          />
        )}
      </div>
    );
  }
  // TOTAL MISSED CLASSES EUR — same sum as Payment for Missed Classes
  if (column.id === "totalMissedClassesEur") {
    const formulaValue = accountFormulas.PaymentforMissedClasses?.formula ?? "";
    return (
      <div className="cell-content total-missed-classes-eur">
        <p>{formatTuitionFeeDisplay(formulaValue, "EUR") || column.value}</p>
      </div>
    );
  }
  // TOTAL NOSTRIFICATIONS EUR — same sum as Nostrification
  if (column.id === "totalNostrificationsEur") {
    const formulaValue = accountFormulas.Nostrification?.formula ?? "";
    return (
      <div className="cell-content total-nostrifications-eur">
        <p>{formatTuitionFeeDisplay(formulaValue, "EUR") || column.value}</p>
      </div>
    );
  }
  // STATUS POPUP (PAYMENTS / EMAIL / EDBO / BALANCES)
  if (STATUS_COLUMNS.has(column.id)) {
    const statusId =
      statusConsts.find((status) => status.label.toLowerCase() === column.value.toLowerCase() || status.id === column.value)?.id ?? "";

    return (
      <div className="cell-content payments">
        <p className={statusId} onClick={() => setIsStatusOpen(true)}>
          {column.value}
        </p>
        {isStatusOpen && (
          <DefaultSelector value={column.value} options={statusConsts} onChange={onChange} onClose={() => setIsStatusOpen(false)} />
        )}
      </div>
    );
  }
  // INVITATION FEE
  if (column.id === "invitationFee") {
    return (
      <div className="cell-content invitation-fee">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsInvitationFeeOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isInvitationFeeOpen && (
          <DefaultInputChange
            title={"Enter invitation fee"}
            value={column.value}
            onChange={onChange}
            onClose={() => setIsInvitationFeeOpen(false)}
          />
        )}
      </div>
    );
  }
  // INVITATION FEE PAID
  if (column.id === "invitationFeePaid") {
    return (
      <div className="cell-content invitation-fee-paid">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsInvitationFeePaidOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isInvitationFeePaidOpen && (
          <CustomIncomeManagement
            title="Income Management in USD"
            mode="invitation"
            accountingId={studentInfoId}
            applicantId={applicantId}
            invitationFee={invitationFee}
            records={invitationFeePaidRecords}
            invoiceOptions={generatedInvoiceNos}
            onRefresh={onRefreshComments}
            onClose={() => setIsInvitationFeePaidOpen(false)}
          />
        )}
      </div>
    );
  }
  // IF DATE
  if (column.id === "ifDate") {
    return (
      <div className="cell-content date">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsIfDateOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isIfDateOpen && <CustomDate value={column.value} onChange={onChange} onClose={() => setIsIfDateOpen(false)} />}
      </div>
    );
  }
  // IF DATE PAID — `date` from first invitation_fee_paid_records object (last added)
  if (column.id === "ifDatePaid") {
    return (
      <div className="cell-content if-date-paid">
        <p>{getLastOfficeAccountingIncomeRecordDate(invitationFeePaidRecords) || column.value}</p>
      </div>
    );
  }
  // PAYMENT FACT USD
  if (column.id === "paymentFactUsd") {
    return (
      <div className="cell-content payment-fact-usd">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPaymentFactUsdOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isPaymentFactUsdOpen && (
          <CustomIncomeManagement
            title="Income Management in USD"
            mode="payment"
            accountingId={studentInfoId}
            applicantId={applicantId}
            totalAmountUsd={totalAmountUsd}
            records={paymentFactUsdRecords}
            invoiceOptions={generatedInvoiceNos}
            onRefresh={onRefreshComments}
            onClose={() => setIsPaymentFactUsdOpen(false)}
          />
        )}
      </div>
    );
  }
  // CREDIT DATE USD — `date` from first payment_fact_usd_records object (last added)
  if (column.id === "creditDateUsd") {
    return (
      <div className="cell-content credit-date-usd">
        <p>{getLastOfficeAccountingIncomeRecordDate(paymentFactUsdRecords) || column.value}</p>
      </div>
    );
  }
  // PAYMENT FACT EUR
  if (column.id === "paymentFactEur") {
    return (
      <div className="cell-content payment-fact-eur">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPaymentFactEurOpen(true)}>
          {getOfficeAccountingEditIcon(column.id)}
        </div>
        {isPaymentFactEurOpen && (
          <CustomIncomeManagement
            title="Income Management in EUR"
            mode="paymentEur"
            accountingId={studentInfoId}
            applicantId={applicantId}
            totalMissedClassesEur={totalMissedClassesEur}
            totalNostrificationsEur={totalNostrificationsEur}
            records={paymentFactEurRecords}
            invoiceOptions={generatedInvoiceNos}
            onRefresh={onRefreshComments}
            onClose={() => setIsPaymentFactEurOpen(false)}
          />
        )}
      </div>
    );
  }
  // CREDIT DATE EUR — `date` from first payment_fact_eur_records object (last added)
  if (column.id === "creditDateEur") {
    return (
      <div className="cell-content credit-date-eur">
        <p>{getLastOfficeAccountingIncomeRecordDate(paymentFactEurRecords) || column.value}</p>
      </div>
    );
  }
  // INVOICE AMOUNT
  if (column.id === "invoiceAmount") {
    return (
      <div className="cell-content invoice-amount">
        <p>{column.value}</p>
      </div>
    );
  }
  /////////////////////////////
  // DISCOUNT
  // if (column.id === "discount") {
  //   return (
  //     <div className="cell-content discount">
  //       <p>{column.value}</p>
  //       <div className="cell-content-edit" onClick={() => setIsDiscountOpen(true)}>
  //         <SidebarSvgExportConst.CustomizationSvg />
  //       </div>
  //       {isDiscountOpen && <CustomDiscount title={"Select an option"} onClose={() => setIsDiscountOpen(false)} />}
  //     </div>
  //   );
  // }
  // // NOTE ADJUSTMENT
  // if (column.id === "noteAdjustment") {
  //   return (
  //     <div className="cell-content note">
  //       <p>{column.value}</p>
  //     </div>
  //   );
  // }

  return <p>{column.value}</p>;
};

export default memo(CellContent, (prev, next) => {
  return (
    prev.column.id === next.column.id &&
    prev.column.value === next.column.value &&
    prev.rowId === next.rowId &&
    prev.studentInfoId === next.studentInfoId &&
    prev.applicantId === next.applicantId &&
    prev.commentsList === next.commentsList &&
    prev.accountFormulas === next.accountFormulas &&
    prev.invitationFee === next.invitationFee &&
    prev.totalAmountUsd === next.totalAmountUsd &&
    prev.totalMissedClassesEur === next.totalMissedClassesEur &&
    prev.totalNostrificationsEur === next.totalNostrificationsEur &&
    prev.invitationFeePaidRecords === next.invitationFeePaidRecords &&
    prev.paymentFactUsdRecords === next.paymentFactUsdRecords &&
    prev.paymentFactEurRecords === next.paymentFactEurRecords &&
    prev.generatedAdmissionNos === next.generatedAdmissionNos &&
    prev.generatedInvoiceNos === next.generatedInvoiceNos &&
    prev.onRefreshComments === next.onRefreshComments &&
    prev.onOpenFullNamePopUp === next.onOpenFullNamePopUp &&
    prev.onChange === next.onChange
  );
});
